import { defineStore } from 'pinia';
import { ref } from 'vue';

import type { DestinationAutocompleteType } from '@apps/main/new-desktop/modules/autoComplete';
import { AutoCompleteType } from '@apps/main/new-desktop/modules/autoComplete';
import { useDestinationSelectorStore } from '@apps/main/new-desktop/core/stores/destinationSelectorStore';
import { useUserLocationStore } from '@apps/main/new-desktop/core/stores';

import type {
  AutoCompleteSuggestion,
  IngredientId,
} from '@libs/trip-planner-interfaces';

import { BuildYourTripModalType } from '../models/buildYourTripModals';

export const useBuildYourTripModalsStore = defineStore(
  'BuildYourTripModalsStore',
  () => {
    const destinationSelectorStore = useDestinationSelectorStore();
    const userLocationStore = useUserLocationStore();

    // State
    const buildYourTripModalType = ref<BuildYourTripModalType>(
      BuildYourTripModalType.None
    );

    // Actions
    function closeBuildYourTripModal(): void {
      buildYourTripModalType.value = BuildYourTripModalType.None;
    }

    function openDestinationSelectorModal(
      searchText: string,
      ingredientId: IngredientId | undefined,
      modalType: DestinationAutocompleteType
    ): void {
      destinationSelectorStore.setDestinationSelectorData(
        searchText,
        ingredientId,
        modalType
      );
      buildYourTripModalType.value = BuildYourTripModalType.DestinationSelector;
    }

    function selectUserPosition(
      autocompleteType: DestinationAutocompleteType,
      placeId?: string
    ): void {
      if (autocompleteType === AutoCompleteType.StartingPosition) {
        userLocationStore.setUserStartLocation(placeId);
      }

      if (autocompleteType === AutoCompleteType.EndingPosition) {
        userLocationStore.setUserEndLocation(placeId);
      }
      destinationSelectorStore.clearDestinationSelectorData();
      closeBuildYourTripModal();
    }

    function confirmDestinationSelectorModal(
      newDestination: AutoCompleteSuggestion | null | undefined
    ): void {
      if (newDestination) {
        destinationSelectorStore.handleConfirmSelection(newDestination);
      }

      closeBuildYourTripModal();
    }

    return {
      // State
      buildYourTripModalType,

      // Actions
      closeBuildYourTripModal,
      openDestinationSelectorModal,
      selectUserPosition,
      confirmDestinationSelectorModal,
    };
  }
);
